import { Link, useSearchParams } from "react-router-dom";
import SiteNavbar from "@/components/feature/SiteNavbar";
import SiteFooter from "@/components/feature/SiteFooter";
import WorkCard from "@/components/feature/WorkCard";
import { inspirations } from "@/mocks/inspirations";
import { works } from "@/mocks/works";
import { useSeo } from "@/hooks/useSeo";
import type { Work } from "@/types";

export default function InspirationPage() {
  const [params] = useSearchParams();
  const topicId = params.get("topic");
  const topic = inspirations.find((i) => i.id === topicId);

  useSeo({
    title: topic
      ? `${topic.title} - 女性ins大片灵感专题`
      : "灵感专题 - 女性ins大片摄影灵感合集",
    description: topic
      ? `${topic.title}：${topic.intro}`
      : "女性ins大片灵感专题，汇集暖光、形体、日常、创意等女性摄影主题合集，为你的拍摄提供源源不断的灵感。",
    keywords: topic
      ? `${topic.title},摄影灵感,女性ins大片,${topic.subtitle}`
      : "摄影灵感,女性ins大片,灵感专题,女性摄影合集,时尚大片分享",
    path: topic ? `/inspiration?topic=${topic.id}` : "/inspiration",
  });

  if (topic) {
    const topicWorks = (works as Work[]).filter((w) => topic.workIds.includes(w.id));
    return (
      <div className="min-h-screen bg-background-50">
        <SiteNavbar overlay />
        <main>
          {/* Topic hero */}
          <section className="relative w-full h-[420px] md:h-[560px] overflow-hidden">
            <img
              src={topic.cover}
              alt={`${topic.title} - 女性摄影灵感专题封面`}
              title={`${topic.title} - 女性摄影灵感专题`}
              className="w-full h-full object-cover object-top animate-slow-zoom"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-foreground-950/80 via-foreground-950/40 to-foreground-950/30" />
            <div className="absolute inset-0 flex items-end">
              <div className="w-full max-w-7xl mx-auto px-4 md:px-8 pb-10 md:pb-14">
                <nav className="flex items-center gap-2 text-xs text-background-100/80 mb-4" aria-label="面包屑">
                  <Link to="/" className="hover:text-accent-400 cursor-pointer">
                    首页
                  </Link>
                  <i className="ri-arrow-right-s-line"></i>
                  <Link to="/inspiration" className="hover:text-accent-400 cursor-pointer">
                    灵感专题
                  </Link>
                  <i className="ri-arrow-right-s-line"></i>
                  <span className="text-background-50">{topic.title}</span>
                </nav>
                <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-accent-500 text-foreground-950 text-xs font-medium mb-4">
                  <i className="ri-lightbulb-line"></i>
                  灵感专题
                </span>
                <h1 className="font-heading text-3xl md:text-5xl font-bold text-background-50 mb-3">
                  {topic.title}
                </h1>
                <p className="text-background-100/85 text-sm md:text-base max-w-2xl">{topic.subtitle}</p>
              </div>
            </div>
          </section>

          <div className="max-w-7xl mx-auto px-4 md:px-8 py-10 md:py-16">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 lg:gap-12">
              <div className="lg:col-span-2">
                <p className="text-base md:text-lg text-foreground-700 leading-relaxed mb-8">
                  {topic.intro}
                </p>
                <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-900 mb-4">
                  专题创作背景
                </h2>
                <p className="text-sm md:text-base text-foreground-600 leading-relaxed mb-4">
                  {topic.story}
                </p>
                <p className="text-xs text-foreground-400 mb-8">{topic.curators}</p>
              </div>
              <aside className="lg:col-span-1">
                <div className="bg-secondary-50 rounded-lg p-6 sticky top-24">
                  <h3 className="font-heading text-base font-semibold text-foreground-900 mb-4">
                    专题信息
                  </h3>
                  <ul className="space-y-3 text-sm text-foreground-600">
                    <li className="flex items-center gap-2">
                      <i className="ri-image-2-line text-primary-500"></i>
                      收录 {topicWorks.length} 件作品
                    </li>
                    <li className="flex items-center gap-2">
                      <i className="ri-palette-line text-primary-500"></i>
                      {topic.subtitle}
                    </li>
                    <li className="flex items-center gap-2">
                      <i className="ri-user-star-line text-primary-500"></i>
                      {topic.curators}
                    </li>
                  </ul>
                  <Link
                    to="/inspiration"
                    className="mt-5 w-full py-2.5 rounded-md border border-background-300 text-sm text-foreground-700 hover:border-primary-400 hover:text-primary-600 transition-colors cursor-pointer whitespace-nowrap flex items-center justify-center gap-1"
                  >
                    <i className="ri-arrow-left-line"></i>
                    返回专题总览
                  </Link>
                </div>
              </aside>
            </div>

            <section className="mt-10 md:mt-14">
              <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-6">
                策划作品集
              </h2>
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-6">
                {topicWorks.map((w, i) => (
                  <WorkCard key={w.id} work={w} index={i} />
                ))}
              </div>
            </section>
          </div>
        </main>
        <SiteFooter />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background-50">
      <SiteNavbar />
      <main className="pt-16 md:pt-[72px]">
        <section className="bg-secondary-50 border-b border-background-200/70">
          <div className="max-w-7xl mx-auto px-4 md:px-8 py-10 md:py-14">
            <nav className="flex items-center gap-2 text-xs text-foreground-500 mb-4" aria-label="面包屑">
              <Link to="/" className="hover:text-primary-600 cursor-pointer">
                首页
              </Link>
              <i className="ri-arrow-right-s-line"></i>
              <span className="text-foreground-700">灵感专题</span>
            </nav>
            <span className="font-label text-xs tracking-[0.25em] uppercase text-primary-500">
              Inspiration
            </span>
            <h1 className="font-heading text-2xl md:text-4xl font-bold text-foreground-900 mt-2">
              灵感专题合集
            </h1>
            <p className="text-sm text-foreground-600 mt-2 max-w-2xl">
              由策展人精心挑选的主题合集，从暖光到形体、从日常到创意，为你的下一次拍摄提供灵感。
            </p>
          </div>
        </section>

        <section className="max-w-7xl mx-auto px-4 md:px-8 py-10 md:py-14">
          {inspirations.length === 0 ? (
            <p className="py-20 text-center text-foreground-500">敬请期待更多精彩专题</p>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {inspirations.map((ins, i) => (
                <Link
                  key={ins.id}
                  to={`/inspiration?topic=${ins.id}`}
                  className="group rounded-lg overflow-hidden bg-background-50 border border-background-200/70 animate-fade-up"
                  style={{ animationDelay: `${i * 60}ms` }}
                >
                  <div className="relative w-full aspect-[3/2] overflow-hidden">
                    <img
                      src={ins.cover}
                      alt={`${ins.title} - 女性摄影灵感专题`}
                      title={`${ins.title} - 女性摄影灵感专题`}
                      className="w-full h-full object-cover object-top transition-transform duration-700 group-hover:scale-105"
                    />
                    <span className="absolute top-3 left-3 px-2.5 py-1 rounded-full bg-accent-500 text-foreground-950 text-[11px] font-medium">
                      灵感专题
                    </span>
                  </div>
                  <div className="p-5">
                    <h2 className="font-heading text-lg font-semibold text-foreground-900 group-hover:text-primary-600 transition-colors">
                      {ins.title}
                    </h2>
                    <p className="text-xs text-foreground-500 mt-1 mb-3">{ins.subtitle}</p>
                    <p className="text-sm text-foreground-600 line-clamp-2 mb-4">{ins.intro}</p>
                    <span className="text-sm font-medium text-primary-600 flex items-center gap-1">
                      查看专题
                      <i className="ri-arrow-right-line transition-transform group-hover:translate-x-1"></i>
                    </span>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </section>
      </main>
      <SiteFooter />
    </div>
  );
}